import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, Package, DollarSign, Hash, ChevronDown, ChevronUp, Clock, Edit, Check, X } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AmmoSettings = ({ onSettingsChange }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    module_enabled: false,
    no_inventory_mode: false,
    ammo_types: [],
    custom_ammo_tags: [],
    non_revenue_tags: ['INNSKYTING'], // Tags that don't count as revenue
    internal_activity_prices: {
      'GJESTE-SKYTING': 0,
      'SIKKERH.KURS': 0,
      'INNSKYTING': 0,
      'KLUBBSTEVNE': 0
    }
  });
  const [newCustomTag, setNewCustomTag] = useState('');
  const [editingDefaultTag, setEditingDefaultTag] = useState(null);
  const [editingDefaultTagName, setEditingDefaultTagName] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAmmo, setNewAmmo] = useState({
    caliber: '',
    rounds_per_box: 50,
    price_per_box: 0,
    current_stock: 0,
    enabled: true
  });
  const [changeLogs, setChangeLogs] = useState([]);
  const [showChangeLogs, setShowChangeLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Auto-refresh change logs every 30 seconds when panel is open
  useEffect(() => {
    let intervalId;
    if (showChangeLogs) {
      intervalId = setInterval(() => {
        fetchChangeLogs();
      }, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [showChangeLogs]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        module_enabled: response.data.module_enabled || false,
        no_inventory_mode: response.data.no_inventory_mode || false,
        ammo_types: response.data.ammo_types || [],
        custom_ammo_tags: response.data.custom_ammo_tags || [],
        non_revenue_tags: response.data.non_revenue_tags || ['INNSKYTING'],
        internal_activity_prices: response.data.internal_activity_prices || {
          'PRØVESKYTING': 0,
          'SIKKERH.KURS': 0,
          'INNSKYTING': 0,
          'KLUBBSTEVNE': 0
        },
        renamed_default_tags: response.data.renamed_default_tags || {}
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ammo settings:', error);
      toast.error('Kunne ikke laste ammo-innstillinger');
      setLoading(false);
    }
  };

  // Rename a default internal activity tag (display name only - preserves data integrity)
  const handleRenameDefaultTag = async (originalTag, newName) => {
    if (!newName.trim()) {
      setEditingDefaultTag(null);
      return;
    }
    const trimmedName = newName.trim();
    const updatedRenamed = { ...(settings.renamed_default_tags || {}), [originalTag]: trimmedName };
    const updatedSettings = { ...settings, renamed_default_tags: updatedRenamed };
    
    setSettings(updatedSettings);
    setEditingDefaultTag(null);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/api/admin/ammo-settings`,
        updatedSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Aktivitet omdøpt til "${trimmedName}"`);
    } catch (error) {
      console.error('Error renaming tag:', error);
      toast.error('Kunne ikke lagre omdøping');
    }
  };

  // Get display name for a tag (respects renames)
  const getTagDisplayName = (tag) => {
    return settings.renamed_default_tags?.[tag] || tag;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      console.log('💾 Saving settings:', settings);
      
      const response = await axios.put(
        `${API_BASE}/api/admin/ammo-settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Settings saved, response:', response.data);
      
      toast.success('Ammo-innstillinger lagret!');
      
      // Notify parent component of settings change
      if (onSettingsChange) {
        onSettingsChange(settings.module_enabled);
      }
      
      // Refresh change logs if the section is open
      if (showChangeLogs) {
        fetchChangeLogs();
      }
      
      // Don't refetch - this causes the toggle to reset
      // Instead, keep the local state as is
    } catch (error) {
      console.error('Error saving ammo settings:', error);
      toast.error('Kunne ikke lagre innstillinger');
    } finally {
      setSaving(false);
    }
  };

  const handleAmmoChange = (index, field, value) => {
    const updated = [...settings.ammo_types];
    updated[index][field] = value;
    setSettings({ ...settings, ammo_types: updated });
  };

  const fetchChangeLogs = async () => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-change-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChangeLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching change logs:', error);
      toast.error('Kunne ikke laste endringslogg');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleAddCustomAmmo = () => {
    if (!newAmmo.caliber.trim()) {
      toast.error('Kalibernavn må fylles ut');
      return;
    }
    
    // Check if caliber already exists
    const exists = settings.ammo_types.some(a => a.caliber.toLowerCase() === newAmmo.caliber.toLowerCase());
    if (exists) {
      toast.error('Dette kaliberet finnes allerede');
      return;
    }

    setSettings({
      ...settings,
      ammo_types: [...settings.ammo_types, { ...newAmmo }]
    });
    
    setNewAmmo({
      caliber: '',
      rounds_per_box: 50,
      price_per_box: 0,
      current_stock: 0,
      enabled: true
    });
    setShowAddModal(false);
    toast.success('Nytt kaliber lagt til');
  };

  const handleDeleteAmmo = (index) => {
    if (!window.confirm('Er du sikker på at du vil fjerne dette kaliberet?')) {
      return;
    }
    
    const updated = settings.ammo_types.filter((_, i) => i !== index);
    setSettings({ ...settings, ammo_types: updated });
  };

  const handleAddCustomTag = async () => {
    const tag = newCustomTag.trim().toUpperCase();
    
    if (!tag) {
      toast.error('Skriv inn en kategori');
      return;
    }
    
    // Check if tag already exists (in custom or default)
    const defaultTags = ['MEDLEM', 'SIKKERH.KURS', 'PRØVESKYTING', 'INNSKYTING'];
    if (defaultTags.includes(tag) || settings.custom_ammo_tags.includes(tag)) {
      toast.error('Denne kategorien finnes allerede');
      return;
    }
    
    const updatedSettings = {
      ...settings,
      custom_ammo_tags: [...settings.custom_ammo_tags, tag]
    };
    
    // Save immediately to backend
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_BASE}/api/admin/ammo-settings`,
        updatedSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSettings(updatedSettings);
      setNewCustomTag('');
      toast.success(`Kategori "${tag}" lagt til og lagret`);
      setSaving(false);
    } catch (error) {
      console.error('Error saving custom tag:', error);
      toast.error('Kunne ikke lagre kategori');
      setSaving(false);
    }
  };

  const handleDeleteCustomTag = async (index) => {
    if (!window.confirm('Er du sikker på at du vil fjerne denne kategorien?')) {
      return;
    }
    
    const updated = settings.custom_ammo_tags.filter((_, i) => i !== index);
    const updatedSettings = { ...settings, custom_ammo_tags: updated };
    
    // Save immediately to backend
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_BASE}/api/admin/ammo-settings`,
        updatedSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSettings(updatedSettings);
      toast.success('Kategori fjernet og lagret');
      setSaving(false);
    } catch (error) {
      console.error('Error deleting custom tag:', error);
      toast.error('Kunne ikke fjerne kategori');
      setSaving(false);
    }
  };

  const handleMigrateTags = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // First do a dry run to see how many will be affected
      const dryRunResponse = await axios.post(
        `${API_BASE}/api/admin/ammo-migrate-tags`,
        { dry_run: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const count = dryRunResponse.data.count;
      
      if (count === 0) {
        toast.success('Ingen salg trenger migrering. Alle har allerede kategorier!');
        setSaving(false);
        return;
      }
      
      // Ask for confirmation with count
      if (!window.confirm(`Dette vil legge til MEDLEM-tag på ${count} gamle salg uten kategori. Fortsette?`)) {
        setSaving(false);
        return;
      }
      
      // Actually perform migration
      const response = await axios.post(
        `${API_BASE}/api/admin/ammo-migrate-tags`,
        { dry_run: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setSaving(false);
    } catch (error) {
      console.error('Error migrating tags:', error);
      toast.error('Kunne ikke migrere tags');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg">
        <p className="text-gray-400">Laster...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Module Enable/Disable */}
      <div className="p-6 bg-gray-800 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src="/patron-symbol.svg" alt="Ammo" className="h-8 w-8" />
            <h3 className="text-lg font-bold text-white">Ammo-salg modul</h3>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.module_enabled}
                onChange={(e) => setSettings({ ...settings, module_enabled: e.target.checked })}
                className="rounded border-gray-600"
              />
              <span className="text-sm text-gray-300">
                {settings.module_enabled ? 'Aktivert' : 'Deaktivert'}
              </span>
            </label>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
            >
              <Save className="h-4 w-4" />
              <span>{saving ? 'Lagrer...' : 'Lagre'}</span>
            </button>
          </div>
        </div>
        
        <div className="p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
          <p className="text-sm text-blue-200">
            Når denne modulen er aktivert kan du registrere ammunisjonssalg på treninger. 
            Beholdning trekkes automatisk fra, og du får oversikt og statistikk på salg.
          </p>
        </div>

        {/* No-inventory mode toggle - lar klubber kun registrere salg uten lagerstyring */}
        {settings.module_enabled && (
          <div className="mt-4 p-4 bg-gray-800 border border-yellow-500/30 rounded-lg" data-testid="ammo-no-inventory-section">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-white font-medium flex items-center gap-2 mb-1">
                  <Package className="w-4 h-4 text-yellow-400" />
                  Bruk ammo-modul uten lagerstyring
                </h4>
                <p className="text-sm text-gray-400">
                  Aktiver dette hvis klubben har lagerstyring i et eksternt kassasystem og kun ønsker å bruke
                  ammo-modulen til lovpålagt registrering av salg som ikke skytes opp på banen.
                  Lagerbeholdning skjules og trekkes <strong>ikke</strong> automatisk fra ved salg.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.no_inventory_mode === true}
                  onChange={(e) => setSettings({ ...settings, no_inventory_mode: e.target.checked })}
                  data-testid="ammo-no-inventory-toggle"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            </div>
            {settings.no_inventory_mode && (
              <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-600/40 rounded text-xs text-yellow-200">
                <strong>Aktivert:</strong> Lagerbeholdning vises ikke, og lager trekkes ikke fra ved salg.
                Husk å oppdatere lager i kassasystemet ditt for korrekt sporing.
              </div>
            )}
          </div>
        )}
      </div>

      {settings.module_enabled && (
        <>
          {/* Ammo Types List */}
          <div className="p-6 bg-gray-800 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium">Ammunisjonstyper</h4>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded flex items-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                Legg til egendefinert kaliber
              </button>
            </div>

            <div className="space-y-3">
              {settings.ammo_types.map((ammo, index) => (
                <div key={index} className="p-4 bg-gray-700 rounded-lg w-full">
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4 w-full">
                    {/* Caliber name - editable */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Kaliber</label>
                      <input
                        type="text"
                        value={ammo.caliber}
                        onChange={(e) => handleAmmoChange(index, 'caliber', e.target.value)}
                        className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm font-medium"
                        placeholder="Navn på kaliber"
                      />
                      <div className="mt-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={ammo.enabled}
                            onChange={(e) => handleAmmoChange(index, 'enabled', e.target.checked)}
                            className="rounded border-gray-600"
                          />
                          <span className="text-xs text-gray-400">Aktiv</span>
                        </label>
                      </div>
                    </div>

                    {/* Rounds per box */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Skudd per eske</label>
                      <input
                        type="number"
                        value={ammo.rounds_per_box}
                        onChange={(e) => handleAmmoChange(index, 'rounds_per_box', parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                        disabled={!ammo.enabled}
                      />
                    </div>

                    {/* Price per box */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Pris per eske (kr)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={ammo.price_per_box}
                        onChange={(e) => handleAmmoChange(index, 'price_per_box', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                        disabled={!ammo.enabled}
                      />
                    </div>

                    {/* Current stock */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Beholdning (esker)</label>
                      <input
                        type="number"
                        value={ammo.current_stock}
                        onChange={(e) => handleAmmoChange(index, 'current_stock', parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                        disabled={!ammo.enabled}
                      />
                    </div>

                    {/* Total rounds */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Totalt skudd</label>
                      <div className="px-2 py-1 bg-gray-900 rounded text-yellow-400 text-sm font-medium">
                        {(ammo.current_stock * ammo.rounds_per_box).toLocaleString()}
                      </div>
                    </div>

                    {/* Delete button */}
                    <div className="flex items-end">
                      <button
                        onClick={() => handleDeleteAmmo(index)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors"
                        title="Fjern kaliber"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {settings.ammo_types.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                Ingen ammunisjonstyper tilgjengelig. Legg til egendefinerte kalibre.
              </div>
            )}
          </div>
        </>
      )}

      {/* Custom Tags Section */}
      {settings.module_enabled && (
        <div className="mt-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
          <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
            <Hash className="h-5 w-5" />
            Egendefinerte kategorier
          </h4>
          <p className="text-sm text-gray-400 mb-4">
            Default kategorier: MEDLEM, SIKKERH.KURS, PRØVESKYTING, INNSKYTING. Legg til egne kategorier under.
          </p>
          
          {/* Add new tag */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newCustomTag}
              onChange={(e) => setNewCustomTag(e.target.value.toUpperCase())}
              placeholder="STEVNE, KURS, osv..."
              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              maxLength={20}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddCustomTag();
                }
              }}
            />
            <button
              onClick={handleAddCustomTag}
              disabled={!newCustomTag.trim()}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 rounded font-medium transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Legg til
            </button>
          </div>

          {/* List of custom tags */}
          {settings.custom_ammo_tags.length > 0 && (
            <div className="space-y-2">
              {settings.custom_ammo_tags.map((tag, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                  <span className="text-white font-medium">{tag}</span>
                  <button
                    onClick={() => handleDeleteCustomTag(index)}
                    className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {settings.custom_ammo_tags.length === 0 && (
            <p className="text-sm text-gray-500 italic">Ingen egendefinerte kategorier lagt til.</p>
          )}
          
          {/* Non-Revenue Categories Section */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Interne aktiviteter (ikke inntekt)</h4>
            <p className="text-xs text-gray-400 mb-3">
              Huk av for kategorier som ikke skal føres som inntekt. Dette gjelder ammunisjon som hentes ut til interne klubbaktiviteter uten betaling.
            </p>
            
            <div className="space-y-2">
              {/* List all categories (default + custom) */}
              {['MEDLEM', 'SIKKERH.KURS', 'PRØVESKYTING', 'INNSKYTING', ...settings.custom_ammo_tags].map((tag) => {
                const isDefault = ['MEDLEM', 'SIKKERH.KURS', 'PRØVESKYTING', 'INNSKYTING'].includes(tag);
                const displayName = getTagDisplayName(tag);
                
                return (
                <div
                  key={tag}
                  className="flex items-center space-x-3 p-2 rounded bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                >
                  <label className="flex items-center space-x-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={settings.non_revenue_tags?.includes(tag) || false}
                      onChange={(e) => {
                      const currentNonRevenue = settings.non_revenue_tags || [];
                      let updatedNonRevenue;
                      let updatedPrices = { ...settings.internal_activity_prices };
                      
                      if (e.target.checked) {
                        // Add tag to non-revenue list
                        updatedNonRevenue = [...currentNonRevenue, tag];
                        // Also add to internal_activity_prices if not already there
                        if (!(tag in updatedPrices)) {
                          updatedPrices[tag] = 0;
                        }
                      } else {
                        // Remove tag from non-revenue list
                        updatedNonRevenue = currentNonRevenue.filter(t => t !== tag);
                        // Remove from internal_activity_prices
                        delete updatedPrices[tag];
                      }
                      
                      const updatedSettings = { ...settings, non_revenue_tags: updatedNonRevenue, internal_activity_prices: updatedPrices };
                      setSettings(updatedSettings);
                      
                      // Auto-save
                      setTimeout(async () => {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.put(
                            `${API_BASE}/api/admin/ammo-settings`,
                            updatedSettings,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          console.log('✅ Non-revenue tags auto-saved');
                        } catch (error) {
                          console.error('Error auto-saving non-revenue tags:', error);
                        }
                      }, 500);
                    }}
                    className="h-4 w-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500"
                  />
                  {editingDefaultTag === tag ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={editingDefaultTagName}
                        onChange={(e) => setEditingDefaultTagName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameDefaultTag(tag, editingDefaultTagName);
                          if (e.key === 'Escape') setEditingDefaultTag(null);
                        }}
                        className="flex-1 px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                        autoFocus
                        data-testid={`rename-input-${tag}`}
                      />
                      <button
                        onClick={() => handleRenameDefaultTag(tag, editingDefaultTagName)}
                        className="p-1 text-green-400 hover:text-green-300"
                        data-testid={`rename-confirm-${tag}`}
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setEditingDefaultTag(null)}
                        className="p-1 text-gray-400 hover:text-gray-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-white">{displayName}</span>
                  )}
                  {settings.non_revenue_tags?.includes(tag) && !editingDefaultTag && (
                    <span className="ml-auto text-xs text-orange-400">(ikke inntekt)</span>
                  )}
                  </label>
                  {/* Edit icon for default tags */}
                  {isDefault && !editingDefaultTag && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingDefaultTag(tag);
                        setEditingDefaultTagName(displayName);
                      }}
                      className="p-1 text-gray-400 hover:text-yellow-400 hover:bg-gray-600 rounded transition-colors"
                      title={`Endre navn på ${displayName}`}
                      data-testid={`edit-default-tag-${tag}`}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                );
              })}
            </div>
            
            {settings.non_revenue_tags && settings.non_revenue_tags.length > 0 && (
              <p className="text-xs text-orange-400 mt-2">
                ⚠️ {settings.non_revenue_tags.length} kategori{settings.non_revenue_tags.length > 1 ? 'er' : ''} markert som interne aktiviteter
              </p>
            )}
          </div>
          
          {/* Internal Activity Prices Section */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Inntekter for Interne aktiviteter</h4>
            <p className="text-xs text-gray-400 mb-4">
              Sett priser for interne aktiviteter. Selv om ammo-salget er 0 kr, kan du ta betalt for selve aktiviteten (f.eks. leie av utstyr, kursavgift, stevnedeltakelse).
            </p>
            
            <div className="space-y-3">
              {Object.entries(settings.internal_activity_prices || {}).map(([activityTag, price]) => (
                <div key={activityTag} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-4 w-4 text-green-400" />
                    <span className="text-white font-medium">{getTagDisplayName(activityTag)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      value={price}
                      onChange={(e) => {
                        const newPrices = {
                          ...settings.internal_activity_prices,
                          [activityTag]: parseInt(e.target.value) || 0
                        };
                        setSettings({ ...settings, internal_activity_prices: newPrices });
                      }}
                      className="w-24 px-3 py-1.5 bg-gray-600 border border-gray-500 rounded text-white text-right"
                    />
                    <span className="text-gray-400 text-sm">kr</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={async () => {
                  setSaving(true);
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(
                      `${API_BASE}/api/admin/ammo-settings`,
                      settings,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Interne aktivitetspriser lagret!');
                    if (onSettingsChange) onSettingsChange();
                  } catch (error) {
                    console.error('Error saving prices:', error);
                    toast.error('Kunne ikke lagre priser');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 text-white rounded font-medium text-sm transition-colors flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Lagrer...' : 'Lagre priser'}
              </button>
            </div>
            
            <p className="text-xs text-gray-500 mt-3">
              💡 Disse inntektene vil vises på Statistikk-siden under "Interne aktiviteter".
            </p>
          </div>
          
          {/* Migration button */}
          <div className="mt-4 pt-4 border-t border-gray-700">
            <button
              onClick={handleMigrateTags}
              disabled={saving}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-400 disabled:bg-gray-600 text-white rounded text-sm transition-colors"
            >
              {saving ? 'Migrerer...' : 'Legg til MEDLEM-tag på gamle salg'}
            </button>
            <p className="text-xs text-gray-400 mt-2">
              Kjør denne én gang for å legge til MEDLEM-tag på alle eksisterende salg som ikke har kategorier.
            </p>
          </div>
        </div>
      )}

      {/* Add Custom Ammo Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Legg til egendefinert kaliber</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Kalibernavn *</label>
                <input
                  type="text"
                  value={newAmmo.caliber}
                  onChange={(e) => setNewAmmo({ ...newAmmo, caliber: e.target.value })}
                  placeholder="F.eks. .357 Magnum"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Skudd per eske *</label>
                <input
                  type="number"
                  value={newAmmo.rounds_per_box}
                  onChange={(e) => setNewAmmo({ ...newAmmo, rounds_per_box: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Pris per eske (kr) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={newAmmo.price_per_box}
                  onChange={(e) => setNewAmmo({ ...newAmmo, price_per_box: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Startbeholdning (esker) *</label>
                <input
                  type="number"
                  value={newAmmo.current_stock}
                  onChange={(e) => setNewAmmo({ ...newAmmo, current_stock: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
              >
                Avbryt
              </button>
              <button
                onClick={handleAddCustomAmmo}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
              >
                Legg til
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Logs Section */}
      <div className="mt-8 pt-6 border-t border-gray-700">
        <button
          onClick={() => {
            setShowChangeLogs(!showChangeLogs);
            if (!showChangeLogs) {
              fetchChangeLogs();
            }
          }}
          className="flex items-center justify-between w-full text-left p-4 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-400" />
            <h4 className="text-sm font-medium text-white">Vis tidligere endringer</h4>
            {changeLogs.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                {changeLogs.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showChangeLogs && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  fetchChangeLogs();
                }}
                className="p-1 hover:bg-gray-600 rounded transition-colors"
                title="Oppdater liste"
              >
                <svg 
                  className={`h-4 w-4 text-gray-400 hover:text-white ${logsLoading ? 'animate-spin' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            {showChangeLogs ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </button>

        {showChangeLogs && (
          <div className="mt-4 space-y-3">
            {logsLoading ? (
              <div className="text-center py-8 text-gray-400">Laster endringslogg...</div>
            ) : changeLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 italic">Ingen endringer registrert ennå.</div>
            ) : (
              changeLogs.map((log, index) => {
                // Handle timestamp parsing safely
                let dateStr = 'Ukjent dato';
                let timeStr = '';
                try {
                  const timestamp = new Date(log.timestamp);
                  if (!isNaN(timestamp.getTime())) {
                    dateStr = timestamp.toLocaleDateString('no-NO');
                    timeStr = timestamp.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
                  }
                } catch (e) {
                  console.warn('Invalid timestamp:', log.timestamp);
                }
                const isSale = log.change_type === 'sale';
                const isDelete = log.change_type === 'delete';
                
                // Different styling for sale vs delete vs settings change
                const bgStyle = isSale 
                  ? 'bg-green-900/20 border-green-700/50' 
                  : isDelete 
                    ? 'bg-red-900/20 border-red-700/50'
                    : 'bg-gray-700/30 border-gray-600';
                
                return (
                  <div key={index} className={`p-4 rounded-lg border ${bgStyle}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h5 className="font-medium text-white flex items-center gap-2">
                          {log.caliber}
                          {isSale && (
                            <span className="text-xs bg-green-600/30 text-green-400 px-2 py-0.5 rounded">SALG</span>
                          )}
                          {isDelete && (
                            <span className="text-xs bg-red-600/30 text-red-400 px-2 py-0.5 rounded">SLETTET</span>
                          )}
                          {!isSale && !isDelete && (
                            <span className="text-xs bg-yellow-600/30 text-yellow-400 px-2 py-0.5 rounded">INNSTILLING</span>
                          )}
                        </h5>
                        <p className="text-xs text-gray-400 mt-1">
                          Endret av: <span className="text-yellow-400">{log.changed_by}</span>
                        </p>
                        {/* Show sale/delete details if available */}
                        {(isSale || isDelete) && log.sale_details && (
                          <div className="mt-2 text-xs space-y-1">
                            <p className={isSale ? 'text-green-400' : 'text-red-400'}>
                              {isSale ? 'Solgt til:' : 'Opprinnelig kjøper:'}{' '}
                              <span className="text-white font-medium">
                                {log.sale_details.member_name}
                                {log.sale_details.is_external && ' (ekstern)'}
                              </span>
                            </p>
                            <p className={isSale ? 'text-green-400' : 'text-red-400'}>
                              Antall: <span className="text-white font-medium">{log.sale_details.boxes_sold} esker</span>
                            </p>
                            {log.sale_details.tags && log.sale_details.tags.length > 0 && (
                              <p className={isSale ? 'text-green-400' : 'text-red-400'}>
                                Kategori: <span className="text-white">{log.sale_details.tags.join(', ')}</span>
                              </p>
                            )}
                            {isDelete && log.sale_details.reason && (
                              <p className="text-red-400 italic">{log.sale_details.reason}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-400">
                        <div>{dateStr}</div>
                        <div>{timeStr}</div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {Object.entries(log.changes).map(([field, values]) => {
                        const fieldLabels = {
                          'rounds_per_box': 'Skudd per eske',
                          'price_per_box': 'Pris per eske',
                          'current_stock': 'Beholdning',
                          'enabled': 'Status'
                        };
                        
                        const formatValue = (val, field) => {
                          if (field === 'enabled') return val ? 'Aktiv' : 'Inaktiv';
                          if (field === 'price_per_box') return `${val} kr`;
                          return val;
                        };
                        
                        return (
                          <div key={field} className="flex items-center gap-2 text-sm">
                            <span className="text-gray-400">{fieldLabels[field] || field}:</span>
                            <span className="text-red-400 line-through">{formatValue(values.old, field)}</span>
                            <span className="text-gray-500">→</span>
                            <span className="text-green-400">{formatValue(values.new, field)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AmmoSettings;
